import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { TText } from "./TText";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../lib/i18n";
import { propertyPath } from "../lib/slug";

const CAT_LABEL = { rural: "Rural", urban: "Urbano", industrial: "Industrial" };

export const PropertyCard = ({ property, onOpen, index = 0 }) => {
  const { lang } = useLanguage();
  const catLabel = lang === "es" ? CAT_LABEL[property.category] : t(lang, `cat_${property.category}`);
  const href = propertyPath(property);

  const handleClick = (e) => {
    if (onOpen) {
      // Allow ctrl/cmd+click or middle-click to open in new tab natively
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      onOpen(property);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8, delay: (index % 6) * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <Link
        to={href}
        onClick={handleClick}
        className="group block text-left w-full focus:outline-none"
        data-testid={`property-card-${property.id}`}
      >
        <div className="relative overflow-hidden aspect-[4/5] bg-[#111822]">
          <img
            src={property.cover_image}
            alt={property.title}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110 select-none"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F16] via-[#0A0F16]/20 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
          <div className="absolute top-4 left-4 text-[10px] tracking-[0.2em] uppercase text-[#C5A059] border border-[#C5A059]/40 px-3 py-1 backdrop-blur-sm bg-[#0A0F16]/40">
            {catLabel}
          </div>
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-[#C5A059] text-[#0A0F16] p-2"><ArrowUpRight size={16} /></div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6">
            <TText
              as="h3"
              text={property.title}
              cacheKey={`prop:${property.id}:title`}
              className="font-serif-display text-2xl md:text-3xl leading-tight text-white mb-1"
            />
            <div className="flex items-center justify-between text-xs text-[#A3A9B2]">
              <TText text={property.location} cacheKey={`prop:${property.id}:loc`} />
              {property.hectares && <span className="text-[#C5A059]">{property.hectares}</span>}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
